import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  IconButton,
  InputAdornment,
  Tooltip,
  Paper,
  List,
  ListItem,
  ListItemText,
  Typography,
  CircularProgress,
  Chip,
  Badge,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  CirclesThreePlus,
  Calendar,
  MagnifyingGlass,
  Lightning,
  Bell,
  ChatCircleText,
  ListChecks,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { canAccessPlatformPreviewFeatures } from '../../utils/platformPreviewAccess';

function AppHeader({ isSupplier = false, _notifications = [], unreadCount = 0, unreadMessagesCount = 0, onNotificationClick }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { user } = useAuth();
  const showSupplierAppsHub =
    isSupplier && canAccessPlatformPreviewFeatures(user);
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const searchRef = useRef(null);
  const [_notificationAnchor, setNotificationAnchor] = useState(null);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchValue.trim().length >= 2) {
        performSearch(searchValue.trim());
      } else {
        setSearchResults([]);
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [searchValue]);

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const performSearch = async (query) => {
    setIsSearching(true);
    try {
      const supplierBusinessId = isSupplier ? localStorage.getItem('currentBusinessId') : null;
      const response = await api.get('/api/search', {
        params: {
          q: query,
          limit: 10,
          ...(supplierBusinessId ? { businessId: supplierBusinessId } : {})
        }
      });
      setSearchResults(response.data.results || []);
      setShowResults(true);
    } catch (error) {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
    if (e.target.value.trim().length >= 2) {
      setShowResults(true);
    }
  };

  const handleSearchFocus = (_e) => {
    if (searchResults.length > 0) {
      setShowResults(true);
    }
  };

  const handleResultClick = (result) => {
    setShowResults(false);
    setSearchValue('');

    if (isSupplier) {
      if (result.type === 'lead') {
        navigate(`/supplier/crm/leads/${result._id}`);
      }
      return;
    }

    // Couples: guests, suppliers, chats
    if (result.type === 'guest') {
      navigate(`/guest-list?guestId=${result._id}`);
    } else if (result.type === 'supplier') {
      navigate(`/dashboard/supplier/${result._id}`);
    } else if (result.type === 'chat') {
      navigate(`/chat/${result._id}`);
    }
  };

  const getModelLabel = (model) => {
    const labels = {
      'Lead': 'CRM Lead',
      'Guest': 'Guest',
      'Supplier': 'Supplier',
      'Chat': 'Message'
    };
    return labels[model] || model;
  };

  const getModelColor = (model) => {
    const colors = {
      'Lead': 'primary',
      'Guest': 'secondary',
      'Supplier': 'success',
      'Chat': 'info'
    };
    return colors[model] || 'default';
  };

  const handleQuickActionOpen = () => {
    navigate(isSupplier ? '/supplier/automations' : '/automations');
  };

  const handleCalendarClick = () => {
    if (isSupplier) {
      navigate('/supplier/bookings?view=calendar');
    } else {
      navigate('/todo-list');
    }
  };

  const handleAppsOrTodoClick = () => {
    if (isSupplier) {
      navigate('/supplier/apps');
    } else {
      navigate('/todo-list');
    }
  };

  const handleSecondaryNavClick = () => {
    if (isSupplier && showSupplierAppsHub) {
      handleAppsOrTodoClick();
      return;
    }
    handleCalendarClick();
  };

  const handleMessagesClick = () => {
    navigate(isSupplier ? '/supplier/messages' : '/chat');
  };

  const handleNotificationClick = (event) => {
    if (onNotificationClick) {
      onNotificationClick(event);
    } else {
      setNotificationAnchor(event.currentTarget);
    }
  };

  const _handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        p: 2,
        width: '100%',
        position: 'sticky',
        top: 0,
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        backdropFilter: 'blur(10px)',
      }}
    >
      {/* Notification Icon */}
      <Tooltip title="Notifications">
        <IconButton
          onClick={handleNotificationClick}
          sx={{
            color: 'text.primary',
            backgroundColor: '#ffffff57',
            borderRadius: '10px',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            },
            '& svg': {
              display: 'block',
            },
          }}
        >
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <Bell size={24} weight="regular" />
          </Badge>
        </IconButton>
      </Tooltip>

      {/* Supplier: Apps hub (admin/test only) or Bookings. Couples: To-Do */}
      <Tooltip title={isSupplier ? (showSupplierAppsHub ? 'Apps' : 'Bookings') : 'To-Do'}>
        <IconButton
          onClick={handleSecondaryNavClick}
          aria-label={isSupplier ? (showSupplierAppsHub ? 'Apps' : 'Bookings') : 'To-Do'}
          sx={{
            color: 'text.primary',
            backgroundColor: '#ffffff57',
            borderRadius: '10px',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            },
            '& svg': {
              display: 'block',
            },
          }}
        >
          {isSupplier
            ? (showSupplierAppsHub
              ? <CirclesThreePlus size={24} weight="regular" />
              : <Calendar size={24} weight="regular" />)
            : <ListChecks size={24} weight="regular" />}
        </IconButton>
      </Tooltip>

      {/* Search Bar */}
      <Box ref={searchRef} sx={{ position: 'relative', flexGrow: 1, maxWidth: 400 }}>
        <TextField
          placeholder="Start your search..."
          value={searchValue}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          size="small"
          sx={{
            width: '100%',
            '& .MuiOutlinedInput-root': {
              backgroundColor: theme.palette.background.paper,
              '& fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.08)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 0, 0, 0.12)',
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <MagnifyingGlass size={20} weight="regular" />
              </InputAdornment>
            ),
            endAdornment: isSearching && (
              <InputAdornment position="end">
                <CircularProgress size={20} />
              </InputAdornment>
            ),
          }}
        />
        
        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              maxHeight: 400,
              overflow: 'auto',
              zIndex: 1300,
              boxShadow: 3,
            }}
          >
            <List dense>
              {searchResults.map((result) => (
                <ListItem
                  key={`${result.type}-${result._id}`}
                  button
                  onClick={() => handleResultClick(result)}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {result.title}
                        </Typography>
                        <Chip
                          label={getModelLabel(result.model)}
                          size="small"
                          color={getModelColor(result.model)}
                          variant="outlined"
                        />
                      </Box>
                    }
                    secondary={result.subtitle}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
        
        {showResults && searchValue.trim().length >= 2 && !isSearching && searchResults.length === 0 && (
          <Paper
            sx={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              mt: 1,
              p: 2,
              zIndex: 1300,
              boxShadow: 3,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              No results found
            </Typography>
          </Paper>
        )}
      </Box>

      {/* Messages (couples) or Automations (supplier) */}
      {isSupplier ? (
        <Tooltip title="Automations">
          <IconButton
            onClick={handleQuickActionOpen}
            sx={{
              color: 'text.primary',
              backgroundColor: '#ffffff57',
              borderRadius: '10px',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
              },
              '& svg': {
                display: 'block',
              },
            }}
          >
            <Lightning size={24} weight="regular" />
          </IconButton>
        </Tooltip>
      ) : (
        <Tooltip title="Messages">
          <IconButton
            onClick={handleMessagesClick}
            sx={{
              color: 'text.primary',
              backgroundColor: '#ffffff57',
              borderRadius: '10px',
              width: 40,
              height: 40,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
              },
              '& svg': {
                display: 'block',
              },
            }}
          >
            <Badge variant="dot" color="error" invisible={unreadMessagesCount === 0}>
              <ChatCircleText size={24} weight="regular" />
            </Badge>
          </IconButton>
        </Tooltip>
      )}

      {/* Lumi Icon - Moved to header, right side */}
      <Tooltip title="Lumi Assistant">
        <IconButton
          onClick={() => navigate(isSupplier ? '/supplier/lumi' : '/lumi')}
          sx={{
            color: 'text.primary',
            backgroundColor: '#ffffff57',
            borderRadius: '10px',
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
            },
            '& img': {
              display: 'block',
            },
          }}
        >
          <Box
            component="img"
            src="/img/lumi.png"
            alt="Lumi"
            sx={{ width: 24, height: 24 }}
          />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default AppHeader;

